import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import SEO from '../components/SEO';
import Hero from '../sections/Hero';
import BeforeAfter from '../sections/BeforeAfter';
import AboutUs from '../sections/AboutUs';
import Philosophy from '../sections/Philosophy';
import Process from '../sections/Process';
import FeaturedProjects from '../sections/FeaturedProjects';
import Partners from '../sections/Partners';
import Awards from '../sections/Awards';
import CtaBanner from '../sections/CtaBanner';
import Services from '../sections/Services';
import Celebrate from '../sections/Celebrate';
import Testimonials from '../sections/Testimonials';
import Footer from '../sections/Footer';

// Awards & Certifications section is currently hidden from the public site.
// Flip to `true` to bring it back.
const SHOW_AWARDS = false;

const indexQuery = graphql`
  {
    sanityHomePage {
      heroSlides {
        _key
        title
        mediaType
        image {
          asset {
            _id
            url
            fluid(maxWidth: 1920) {
              ...GatsbySanityImageFluid
            }
          }
        }
        video {
          asset {
            url
          }
        }
        project {
          title
          slug {
            current
          }
        }
      }
    }
    sanityBeforeAfterSection {
      sectionTag
      sectionTitle
      items {
        _key
        title
        beforeImage {
          asset {
            _id
            url
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
        afterImage {
          asset {
            _id
            url
            fluid(maxWidth: 800) {
              ...GatsbySanityImageFluid
            }
          }
        }
      }
    }
    sanityAboutSection {
      sectionTag
      sectionTitle
      description
      image {
        asset {
          _id
          url
          fluid(maxWidth: 800) {
            ...GatsbySanityImageFluid
          }
        }
      }
      stats {
        _key
        number
        suffix
        label
      }
    }
    sanityPhilosophySection {
      sectionTag
      sectionTitle
      items {
        _key
        title
        description
        image {
          asset {
            _id
            url
          }
        }
      }
    }
    sanityProcessSection {
      sectionTag
      sectionTitle
      steps {
        _key
        title
        description
        icon {
          asset {
            _id
            url
          }
        }
      }
    }
    sanityFeaturedSection {
      sectionTag
      sectionTitle
      subtitle
      projects {
        _id
        title
        slug {
          current
        }
        category {
          _id
          name
        }
        coverImage {
          asset {
            _id
            url
          }
        }
      }
    }
    allSanityProject {
      totalCount
    }
    allSanityProjectCategory(sort: { fields: position, order: ASC }) {
      nodes {
        _id
        name
      }
    }
    sanityCtaBannerSection {
      title
      ctaLabel
      ctaLink
      backgroundImage {
        asset {
          _id
          url
        }
      }
    }
    sanityAwardsSection {
      sectionTag
      sectionTitle
      ctaLabel
      ctaLink
      logos {
        asset {
          _id
          url
        }
      }
    }
    sanityPartnersSection {
      sectionTag
      sectionTitle
      description
      logos {
        asset {
          _id
          url
        }
      }
    }
    sanityServicesSection {
      sectionTag
      sectionTitle
      certTitle
      certDescription
      certLogo {
        asset {
          _id
          url
        }
      }
      items {
        _key
        title
        description
        icon {
          asset {
            _id
            url
          }
        }
      }
    }
    sanityTestimonialSection {
      sectionTag
      sectionTitle
    }
    allSanityTestimonial(sort: { fields: order, order: ASC }) {
      nodes {
        _id
        name
        position
        company
        content
        rating
        photo {
          asset {
            _id
            url
          }
        }
        screenshot {
          asset {
            _id
            url
          }
        }
      }
    }
    sanityCelebrateSection {
      sectionTag
      sectionTitle
      description
      images {
        asset {
          _id
          url
        }
      }
    }
  }
`;

export default function IndexPage() {
  const data = useStaticQuery(indexQuery);
  const homePage = data?.sanityHomePage;
  const beforeAfter = data?.sanityBeforeAfterSection;
  const about = data?.sanityAboutSection;
  const philosophy = data?.sanityPhilosophySection;
  const process = data?.sanityProcessSection;
  const featured = data?.sanityFeaturedSection;
  const totalProjects = data?.allSanityProject?.totalCount || 0;
  const categoryNodes = data?.allSanityProjectCategory?.nodes || [];
  const partners = data?.sanityPartnersSection;
  const awards = data?.sanityAwardsSection;
  const ctaBanner = data?.sanityCtaBannerSection;
  const services = data?.sanityServicesSection;
  const testimonialSection = data?.sanityTestimonialSection;
  const testimonialNodes = data?.allSanityTestimonial?.nodes || [];
  const celebrate = data?.sanityCelebrateSection;

  return (
    <div>
      <SEO title="Decorzo Space - Interior Design & Home Renovation" />
      <Hero slides={homePage?.heroSlides} />
      <AboutUs
        tag={about?.sectionTag}
        title={about?.sectionTitle}
        description={about?.description}
        image={about?.image}
        stats={about?.stats}
      />
      <BeforeAfter
        tag={beforeAfter?.sectionTag}
        title={beforeAfter?.sectionTitle}
        items={beforeAfter?.items}
      />
      <Philosophy
        tag={philosophy?.sectionTag}
        title={philosophy?.sectionTitle}
        items={philosophy?.items}
      />
      <Celebrate
        tag={celebrate?.sectionTag}
        title={celebrate?.sectionTitle}
        description={celebrate?.description}
        images={celebrate?.images}
      />
      <Process
        tag={process?.sectionTag}
        title={process?.sectionTitle}
        steps={process?.steps}
      />
      <FeaturedProjects
        tag={featured?.sectionTag}
        title={featured?.sectionTitle}
        subtitle={featured?.subtitle}
        projects={featured?.projects}
        categories={categoryNodes}
        totalCount={totalProjects}
      />
      <Services
        tag={services?.sectionTag}
        title={services?.sectionTitle}
        certTitle={services?.certTitle}
        certDescription={services?.certDescription}
        certLogo={services?.certLogo}
        items={services?.items}
      />
      <Testimonials
        tag={testimonialSection?.sectionTag}
        title={testimonialSection?.sectionTitle}
        testimonials={testimonialNodes}
      />
      <Partners
        tag={partners?.sectionTag}
        title={partners?.sectionTitle}
        description={partners?.description}
        logos={partners?.logos}
      />
      {/* Awards & Certifications — hidden from the public site.
          Content is still managed in the CMS; re-render this block to restore. */}
      {SHOW_AWARDS && (
        <Awards
          tag={awards?.sectionTag}
          title={awards?.sectionTitle}
          ctaLabel={awards?.ctaLabel}
          ctaLink={awards?.ctaLink}
          logos={awards?.logos}
        />
      )}
      <CtaBanner
        title={ctaBanner?.title}
        ctaLabel={ctaBanner?.ctaLabel}
        ctaLink={ctaBanner?.ctaLink}
        backgroundImage={ctaBanner?.backgroundImage}
      />
      <Footer />
    </div>
  );
}
